import Vue from "vue";
import ZonepartsModel from "../../DriveScout/API/models/ZonepartsModel";
import GeometryService from "../../app/admin/directives/service-area-editor/services/GeometryService";

// Create ZonepartsModel instance
const zonepartsModel = new ZonepartsModel();

const state = {
  zones: [],
  zoneparts: [],
  cleanZoneparts: [], // Last saved state for cancel/revert functionality
  selectedFeature: null,
  activeTool: null, // 'select', 'new-sector', 'edit-shape', 'move', 'delete', or null
  isDirty: false,
  // Non-reactive geometry cache for map operations
  geometryCache: new Map(),
  // Location pins visibility
  showLocationPins: true,
};

const mutations = {
  SET_ZONES(state, zones) {
    state.zones = zones;
  },

  SET_ZONEPARTS(state, zoneparts) {
    state.zoneparts = zoneparts;
    // Clear geometry cache when setting new zoneparts
    state.geometryCache.clear();
  },

  UPDATE_ZONEPART(state, { zonepartId, data }) {
    const index = state.zoneparts.findIndex((zp) => zp.id === zonepartId);
    if (index !== -1) {
      Vue.set(state.zoneparts, index, { ...state.zoneparts[index], ...data });
      // Remove cached geometry for this zonepart as it may have changed
      state.geometryCache.delete(zonepartId);
    }
  },

  REMOVE_ZONEPART(state, zonepartId) {
    const index = state.zoneparts.findIndex((zp) => zp.id === zonepartId);
    if (index !== -1) {
      state.zoneparts.splice(index, 1);
      // Remove cached geometry for deleted zonepart
      state.geometryCache.delete(zonepartId);
    }
  },

  REMOVE_ZONE(state, zoneId) {
    const index = state.zones.findIndex((z) => z.id === zoneId);
    if (index !== -1) {
      state.zones.splice(index, 1);
    }
  },

  SET_SELECTED_FEATURE(state, feature) {
    state.selectedFeature = feature;
  },

  SET_ACTIVE_TOOL(state, tool) {
    state.activeTool = tool;
  },

  SET_DIRTY(state, isDirty) {
    state.isDirty = isDirty;
  },

  SET_CLEAN_STATE(state) {
    // Store current zoneparts as the clean state (after successful save)
    state.cleanZoneparts = JSON.parse(JSON.stringify(state.zoneparts));
  },

  RESET_TO_CLEAN_STATE(state) {
    // Revert zoneparts to the last saved clean state
    state.zoneparts = JSON.parse(JSON.stringify(state.cleanZoneparts));
    state.isDirty = false;
    // Clear geometry cache as we're reverting
    state.geometryCache.clear();
  },

  CACHE_GEOMETRY(state, { zonepartId, geometry }) {
    // Store non-reactive geometry in cache
    const cleanGeometry = GeometryService.createNonReactiveClone(geometry);
    state.geometryCache.set(zonepartId, Object.freeze(cleanGeometry));
  },

  CLEAR_GEOMETRY_CACHE(state) {
    state.geometryCache.clear();
  },

  REMOVE_CACHED_GEOMETRY(state, zonepartId) {
    state.geometryCache.delete(zonepartId);
  },

  TOGGLE_LOCATION_PINS(state) {
    state.showLocationPins = !state.showLocationPins;
  },
};

const actions = {
  async fetchZones({ commit }, schoolId) {
    try {
      // Since we can only use zoneparts endpoints, fetch zoneparts and derive zones
      const zoneparts = await zonepartsModel.getZoneparts(schoolId);

      // Extract unique zones from zoneparts data
      const zonesMap = new Map();
      zoneparts.forEach((zonepart) => {
        if (zonepart.zone && !zonesMap.has(zonepart.zone.id)) {
          zonesMap.set(zonepart.zone.id, {
            id: zonepart.zone.id,
            name: zonepart.zone.name,
            location_id: zonepart.zone.location_id, // Include location_id for zone
          });
        }
      });

      const zones = Array.from(zonesMap.values());
      commit("SET_ZONES", zones);
      return zones;
    } catch (error) {
      console.error("Failed to fetch zones:", error);
      throw error;
    }
  },

  async fetchZoneparts({ commit }, schoolId) {
    try {
      const zoneparts = await zonepartsModel.getZoneparts(schoolId);
      commit("SET_ZONEPARTS", zoneparts);
      // Also set as clean state since this is fresh from server
      commit("SET_CLEAN_STATE");
      return zoneparts;
    } catch (error) {
      console.error("Failed to fetch zoneparts:", error);
      throw error;
    }
  },

  async updateZonepart({ commit }, { schoolId, zonepartId, data }) {
    try {
      const updatedZonepart = await zonepartsModel.updateZonepart(
        schoolId,
        zonepartId,
        data,
      );
      commit("UPDATE_ZONEPART", { zonepartId, data: updatedZonepart });
      return updatedZonepart;
    } catch (error) {
      console.error("Failed to update zonepart:", error);
      throw error;
    }
  },

  async saveAllChanges({ commit, state }, { schoolId, pendingUpdates }) {
    try {
      console.log("Saving all changes:", pendingUpdates);
      const results = [];

      // Save each update via API
      for (const update of pendingUpdates) {
        const result = await zonepartsModel.updateZonepart(
          schoolId,
          update.id,
          update.data,
        );
        commit("UPDATE_ZONEPART", { zonepartId: update.id, data: result });
        results.push(result);
      }

      // Clear dirty flag after successful save
      commit("SET_DIRTY", false);
      // Update clean state after successful save
      commit("SET_CLEAN_STATE");

      return results;
    } catch (error) {
      console.error("Failed to save changes:", error);
      throw error;
    }
  },

  async saveSelectedZonepart({ commit, state, rootState }) {
    try {
      // Get the selected zonepart
      if (!state.selectedFeature || state.selectedFeature.type !== "zonepart") {
        throw new Error("No zonepart selected");
      }

      const selectedId = state.selectedFeature.id;
      const zonepart = state.zoneparts.find((zp) => zp.id === selectedId);

      if (!zonepart) {
        throw new Error("Selected zonepart not found");
      }

      // Get school ID from somewhere (might need to pass as parameter)
      // For now, assuming it's available in the zonepart or needs to be passed
      const schoolId = zonepart.school_id || 1; // You may need to adjust this

      // Save only this zonepart
      const result = await zonepartsModel.updateZonepart(
        schoolId,
        selectedId,
        zonepart,
      );
      commit("UPDATE_ZONEPART", { zonepartId: selectedId, data: result });

      // Clear dirty flag after successful save
      commit("SET_DIRTY", false);
      // Update clean state after successful save
      commit("SET_CLEAN_STATE");

      return result;
    } catch (error) {
      console.error("Failed to save selected zonepart:", error);
      throw error;
    }
  },

  async createZonepart({ dispatch, commit, state }, newZonepart) {
    try {
      // Create the zonepart via API
      const createdZonepart = await zonepartsModel.createZonepart(
        newZonepart.school_id,
        newZonepart,
      );

      // If a new zone was created, immediately add it to the zones array
      // This prevents race condition where zonepart renders before zone exists
      if (createdZonepart.zone && createdZonepart.zone.id) {
        const existingZone = state.zones.find(
          (z) => z.id === createdZonepart.zone.id,
        );
        if (!existingZone) {
          // Add the new zone to state immediately
          const updatedZones = [
            ...state.zones,
            {
              id: createdZonepart.zone.id,
              name: createdZonepart.zone.name,
              location_id: createdZonepart.zone.location_id, // Include location_id
            },
          ];
          commit("SET_ZONES", updatedZones);
        }
      }

      // Add to state
      const currentZoneparts = [...state.zoneparts, createdZonepart];
      commit("SET_ZONEPARTS", currentZoneparts);

      // Still refresh zones for data consistency
      // This ensures we have the complete zone data from the server
      if (newZonepart.zone_id === 0 && newZonepart.zone_name) {
        // The API should have created the zone, so refresh zones
        await dispatch("fetchZones", newZonepart.school_id);
      }

      return createdZonepart;
    } catch (error) {
      console.error("Failed to create zonepart:", error);
      throw error;
    }
  },

  async deleteZonepart({ commit, state }, { schoolId, zonepartId }) {
    try {
      // Get the zonepart before deletion to know its zone
      const zonepart = state.zoneparts.find((zp) => zp.id === zonepartId);
      if (!zonepart) {
        throw new Error("Zonepart not found");
      }

      // Call API to delete the zonepart
      const result = await zonepartsModel.deleteZonepart(schoolId, zonepartId);

      // Remove zonepart from state
      commit("REMOVE_ZONEPART", zonepartId);

      // If the zone was deleted (no more sectors), remove it from state
      if (result.zone_deleted) {
        commit("REMOVE_ZONE", result.zone_id);
      }

      // Return result with zone info for centering logic
      return {
        ...result,
        deletedZonepart: zonepart,
      };
    } catch (error) {
      console.error("Failed to delete zonepart:", error);
      throw error;
    }
  },

  async refreshData({ dispatch }, schoolId) {
    try {
      // Refresh both zones and zoneparts from server
      await Promise.all([
        dispatch("fetchZones", schoolId),
        dispatch("fetchZoneparts", schoolId),
      ]);
    } catch (error) {
      console.error("Failed to refresh data:", error);
      throw error;
    }
  },

  selectFeature({ commit }, feature) {
    commit("SET_SELECTED_FEATURE", feature);
  },

  setActiveTool({ commit }, tool) {
    commit("SET_ACTIVE_TOOL", tool);
  },

  setDirty({ commit }, isDirty) {
    commit("SET_DIRTY", isDirty);
  },

  resetToCleanState({ commit }) {
    commit("RESET_TO_CLEAN_STATE");
  },

  setCleanState({ commit }) {
    commit("SET_CLEAN_STATE");
  },

  clearSelection({ commit }) {
    commit("SET_SELECTED_FEATURE", null);
  },

  cacheGeometry({ commit }, { zonepartId, geometry }) {
    commit("CACHE_GEOMETRY", { zonepartId, geometry });
  },

  clearGeometryCache({ commit }) {
    commit("CLEAR_GEOMETRY_CACHE");
  },

  removeCachedGeometry({ commit }, zonepartId) {
    commit("REMOVE_CACHED_GEOMETRY", zonepartId);
  },

  toggleLocationPins({ commit }) {
    commit("TOGGLE_LOCATION_PINS");
  },
};

const getters = {
  activeTool: (state) => state.activeTool,

  getZoneById: (state) => (zoneId) => {
    return state.zones.find((zone) => zone.id === zoneId);
  },

  getZonepartsByZoneId: (state) => (zoneId) => {
    return state.zoneparts.filter((zonepart) => zonepart.zone_id === zoneId);
  },

  selectedFeatureGeometry: (state) => {
    if (!state.selectedFeature) {
      return null;
    }

    if (state.selectedFeature.type === "zonepart") {
      const zonepart = state.zoneparts.find(
        (zp) => zp.id === state.selectedFeature.id,
      );
      return zonepart?.geometry;
    } else if (state.selectedFeature.type === "zone") {
      // For zones, we might want to merge all zonepart geometries
      // This would require a geometry library like turf.js
      return null;
    }

    return null;
  },

  hasUnsavedChanges: (state) => state.isDirty,

  zonepartsWithGeometry: (state) => {
    return state.zoneparts.filter((zp) => zp.geometry !== null);
  },

  zonesWithZonepartCounts: (state) => {
    return state.zones.map((zone) => ({
      ...zone,
      zonepartCount: state.zoneparts.filter((zp) => zp.zone_id === zone.id)
        .length,
    }));
  },

  getCachedGeometry: (state) => (zonepartId) => {
    return state.geometryCache.get(zonepartId);
  },

  getCleanGeometry: (state, getters) => (zonepartId) => {
    // First check cache
    const cached = state.geometryCache.get(zonepartId);
    if (cached) {
      return cached;
    }

    // If not cached, get from zonepart and clean it
    const zonepart = state.zoneparts.find((zp) => zp.id === zonepartId);
    if (zonepart && zonepart.geometry) {
      const cleanGeometry = GeometryService.createNonReactiveClone(
        zonepart.geometry,
      );
      return cleanGeometry;
    }

    return null;
  },

  getZonepartsWithCleanGeometry: (state, getters) => {
    return state.zoneparts
      .map((zonepart) => ({
        ...zonepart,
        cleanGeometry: getters.getCleanGeometry(zonepart.id),
      }))
      .filter((zp) => zp.cleanGeometry !== null);
  },

  schoolLocations: (state, getters, rootState) => {
    // Access the school's locations from the auth store
    return rootState.auth?.school?.locations || [];
  },

  schoolLocationsWithCoordinates: (state, getters, rootState) => {
    // Filter school locations that have lat/lng coordinates
    const locations = rootState.auth?.school?.locations || [];
    return locations.filter(
      (location) =>
        location.lat !== null &&
        location.lat !== undefined &&
        location.lng !== null &&
        location.lng !== undefined,
    );
  },

  showLocationPins: (state) => state.showLocationPins,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
